// @flow
/* eslint-disable no-nested-ternary */
import {
  createSelector,
} from 'reselect';
import {
  DateTime,
} from 'luxon';
import * as R from 'ramda';

import type {
  ID,
  State,
  Selector,
  IntervalType,
  WorklogResource,
  WorklogMap,
  WorklogWithIssue,
  WorklogWithIssueMap,
  WorklogsGroupedByDate,
  WorklogsGroupedByDateAndIssue,
} from 'chronos-types';

import {
  getResourceMap,
} from './resources';
import {
  getUiState,
} from './ui';
import {
  getProfile,
} from './profile';


const dateAndIssueFilter = (
  (
    issuesList: Array<ID>,
    interval: IntervalType,
  ) => (
    (worklog: WorklogResource): boolean => (
      issuesList.includes(worklog.issueId)
        ? (
          (interval.length('days') > 1
            ? interval.contains(DateTime.fromISO(worklog.started))
            : interval.start.hasSame(DateTime.fromISO(worklog.started), 'day'))
        ) : false
    )
  )
);

export const getWorklogsDateAndIssueFiltered: Selector<State, any, WorklogMap> = createSelector(
  [
    getUiState('calendarDate'),
    getResourceMap('worklogs'),
    s => s.issues.lists.issues,
  ],
  (
    interval,
    allWorklogs,
    issuesList,
  ) => R.pickBy(
    dateAndIssueFilter(
      issuesList,
      interval,
    ),
    allWorklogs,
  ),
);

const getWorklogCommentText = worklog => (
  (worklog.comment?.content || []).find(
    i => (
      i.type === 'paragraph'
      && i?.content[0]?.type === 'text'
      && !i?.content[0]?.marks
    ),
  )
  || ({
    content: [{
      text: '',
    }],
  })
).content[0].text;

export const getWorklogsMapWithIssueInfo: Selector<State, any, WorklogWithIssueMap> = (
  createSelector(
    [
      getWorklogsDateAndIssueFiltered,
      getResourceMap('issues'),
    ],
    (
      worklogs,
      issues,
    ) => R.map(
      (w: WorklogResource): WorklogWithIssue => ({
        ...w,
        commentText: (
          typeof w.comment === 'string'
            ? w.comment
            : getWorklogCommentText(w)
        ),
        issue: issues[w.issueId],
      }),
      worklogs,
    ),
  )
);

export const getWorklogsFiltered: Selector<State, any, WorklogWithIssueMap> = createSelector(
  [
    getWorklogsMapWithIssueInfo,
    getUiState('sidebarSelection'),
    ({ filters }) => filters,
    getProfile,
  ],
  (
    worklogs,
    selected,
    filtersRaw,
    profile,
  ) => {
    const {
      filterType: garbage,
      ...filters
    } = filtersRaw;
    const filterUsers = [...filters.users];
    if (
      !filterUsers.length
      && (
        Object.keys(filters).reduce(
          (acc, filterName) => (
            acc
            && filters[filterName].length === 0
          ),
          true,
        )
      )
    ) {
      filterUsers.push(
        profile.userData.accountId
        || profile.userData.key,
      );
    }
    const worklogFilter = (worklog) => (
      (
        filterUsers.length
          ? (
            filterUsers.includes(
              worklog.author.accountId
              || worklog.author.key,
            )
          )
          : true
      )
      && (
        !selected.issues.length
        || R.contains(
          worklog.issueId,
          selected.issues,
        )
      )
      && (
        !selected.projects.length
        || R.contains(
          worklog.issue.fields.project.id,
          selected.projects,
        )
      )
      && (
        !selected.users.length
        || R.contains(
          (
            worklog.author.accountId
            || worklog.author.key
          ),
          selected.users,
        )
      )
    );
    return R.pickBy(
      worklogFilter,
      worklogs,
    );
  },
);

export const getWorklogsGroupedByISODate: Selector<State, any, WorklogsGroupedByDate> = (
  createSelector(
    getWorklogsFiltered,
    worklogs => R.groupBy(
      w => w.started.slice(0, 10),
      R.values(worklogs),
    ),
  )
);

// eslint-disable-next-line max-len
export const getWorklogsMapGroupedByISODateAndIssue = createSelector(
  [
    getWorklogsGroupedByISODate,
    getResourceMap('activities'),
  ],
  (
    groupedByISODate,
    activities,
  ) => {
    const groupedByISODateAndIssue = R.map(
      R.groupBy(
        w => w.issueId,
      ),
      groupedByISODate,
    );

    const joinWorklogs = R.map(
      (issueGroup) => {
        return ({
          issue: issueGroup[0].issue,
          author: issueGroup[0].author,
          timeSpentSeconds: issueGroup.reduce(
            (value, w) => value + w.timeSpentSeconds,
            0,
          ),
          worklogs: issueGroup.map(w => ({ ...w, isScreenshotsExists: activities[w.id] !== undefined })),
          isScreenshotsExists: issueGroup.some(w => activities[w.id]),
        })
      }
    );

    const result = R.map(
      joinWorklogs,
      groupedByISODateAndIssue,
    );

    return R.map(
      (day) => {
        return ({
          ...day,
          isScreenshotsExists: (
            Object.keys(day).some((issueId) => (day[issueId]?.isScreenshotsExists || false))
          ),
        });
      },
      result,
    );
  },
);

// eslint-disable-next-line max-len
export const getWorklogsMapGroupedByISODate: Selector<State, any, WorklogsGroupedByDateAndIssue> = createSelector(
  [
    getWorklogsGroupedByISODate,
    getResourceMap('worklogs'),
    getUiState('groupBy'),
  ],
  (
    worklogs,
    worklogsMap,
    groupBy,
  ) => {
    if (groupBy === 'users') {
      return R.mapObjIndexed(
        (item, date) => (
          item.reduce(
            (acc, worklog) => ({
              ...acc,
              [worklog.author.accountId || worklog.author.key]: {
                timeSpentSeconds: (
                  (acc[worklog.author.accountId || worklog.author.key]?.timeSpentSeconds || 0)
                    + (
                      worklog.started.split('T')[0] === date
                        ? worklog.timeSpentSeconds
                        : 0
                    )
                ),
              },
            }),
            {},
          )
        ),
        worklogs,
      );
    }
    if (groupBy === 'projects') {
      return R.mapObjIndexed(
        (item, date) => (
          item.reduce(
            (acc, worklog) => ({
              ...acc,
              [worklog.issue.fields.project.id]: {
                timeSpentSeconds: (
                  (acc[worklog.issue.fields.project.id]?.timeSpentSeconds || 0)
                    + (
                      worklog.started.split('T')[0] === date
                        ? worklog.timeSpentSeconds
                        : 0
                    )
                ),
              },
            }),
            {},
          )
        ),
        worklogs,
      );
    }
    return R.mapObjIndexed(
      (item, date) => (
        item.reduce(
          (acc, worklog) => ({
            ...acc,
            [worklog.issue.id]: {
              timeSpentSeconds: (
                (acc[worklog.issue.id]?.timeSpentSeconds || 0)
                  + (
                    worklog.started.split('T')[0] === date
                      ? worklog.timeSpentSeconds
                      : 0
                  )
              ),
            },
          }),
          {},
        )
      ),
      worklogs,
    );
  },
);

export const getEditWorklog = createSelector(
  [
    getResourceMap('worklogs'),
    getResourceMap('issues'),
    getUiState('editWorklogId'),
  ],
  (
    worklogsMap: WorklogsResources,
    issuesMap: IssuesResources,
    worklogId: Id,
  ) => (
    worklogsMap[worklogId]
      ? ({
        ...worklogsMap[worklogId],
        issue: issuesMap[worklogsMap[worklogId].issueId],
        commentText: (
          typeof worklogsMap[worklogId].comment === 'string'
            ? worklogsMap[worklogId].comment
            : getWorklogCommentText(worklogsMap[worklogId])
        ),
      }) : (
        null
      )
  ),
);
